import { useContext, useEffect, useRef, useState } from 'react';
import { PushContext, PushMessage } from '@hooks/usePush/PushContext.tsx';

interface ChannelCallbacks {
  /** job.progress — counters + optionally a compact item summary in item.resultJson */
  onProgress?: (event: PushMessage) => void;
  /** job.finished — a run (validation or execution) ended; refetch the detail once */
  onFinished?: (event: PushMessage) => void;
  /** job.draft.changed — another viewer edited/deleted a row, started execute/revalidate, or deleted the draft */
  onDraftChanged?: (event: PushMessage) => void;
}

/**
 * A job detail page's live source: the per-job Centrifugo channel (job:<jobId>, client-side
 * subscription with a BE-minted token). Works identically for the creator and same-company
 * colleagues — the personal-channel job store only carries the creator's jobs. `live` =
 * subscription confirmed; drives the page's poll-fallback cadence (15 s down / 60 s insurance).
 */
export const useJobChannel = (jobId: string, callbacks: ChannelCallbacks) => {
  const { subscribeChannel } = useContext(PushContext);
  const [live, setLive] = useState(false);

  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  useEffect(() => {
    return subscribeChannel(`job:${jobId}`, {
      onPublication: message => {
        if (message.jobId != null && message.jobId !== jobId) return;
        if (message.type === 'job.progress') callbacksRef.current.onProgress?.(message);
        if (message.type === 'job.finished') callbacksRef.current.onFinished?.(message);
        if (message.type === 'job.draft.changed') callbacksRef.current.onDraftChanged?.(message);
      },
      onStateChange: setLive,
    });
  }, [jobId]);

  return { live };
};
